<template>
    <div class="group-info-view">
        <div class="ma-4">
            <div class="d-flex flex-row">
                <v-btn variant="elevated"
                       color="primary"
                       icon="mdi-keyboard-backspace"
                       @click="goBack"
                       class="mr-4" />

                <h1 class="text-primary">{{group.name}}</h1>

                <v-btn @click="showInviteDialog = true"
                       class="ml-4 mt-2"
                       color="lonely"
                       variant="flat">Пригласить</v-btn>
            </div>

            <h2 class="text-lonely my-4">Данные группы:</h2>

            <GroupInfoList :group="group" />

            <h2 class="text-lonely mt-4">Члены группы:</h2>

            <v-data-table hover
                          width="auto"
                          :headers="getHeaders()"
                          :items="members" />
        </div>

        <v-dialog v-model="showInviteDialog"
                  min-width="300"
                  width="600"
                  transition="dialog-bottom-transition">
            <v-card class="text-primary ma-0">
                <v-card-title>
                    Пригласить пользователя
                </v-card-title>

                <v-card-text class="pa-4 py-0">
                    <v-btn @click="showEmailDialog = true"
                           class="w-100 mb-2"
                           color="primary"
                           variant="outlined">
                        Пригласить по email
                    </v-btn>
                    <v-btn @click="onShowGroupDialog"
                           class="w-100 mb-2"
                           color="primary"
                           variant="outlined">
                        Пригласить из группы
                    </v-btn>
                </v-card-text>

                <v-card-actions class="mb-2 mr-2">
                    <v-spacer />
                    <v-btn color="secondary"
                           variant="elevated"
                           @click="showInviteDialog = false">
                        Отменить
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <SelectGroupsMemberDialogs v-model="showSelectGroupMemberDialog"
                                   :groups="userGroups"
                                   :filter-groups-ids="!group ? [] : [group.id]"
                                   :filter-users-ids="invitedUsersIds"
                                   @select:member="onSelectUser($event)" />

        <v-dialog v-model="showInviteUserDialog"
                  min-width="300"
                  width="600"
                  transition="dialog-bottom-transition">
            <v-card class="text-primary ma-0">
                <v-card-title>
                    Пригласить пользователя {{selectedUser?.userName}}
                </v-card-title>

                <v-card-text class="pa-4 py-0">
                    <v-text-field class="mt-0 mb-1"
                                  readonly
                                  label="Заголовок приглашения"
                                  :modelValue="invitationTitle" />
                    <v-text-field class="mt-0 mb-1"
                                  label="Сообщение приглашения"
                                  v-model.trim="invitationMessage" />
                </v-card-text>

                <v-card-actions class="mb-2 mr-2">
                    <v-spacer />
                    <v-btn color="secondary"
                           variant="elevated"
                           @click="showInviteUserDialog = false">
                        Отменить
                    </v-btn>
                    <v-btn color="primary"
                           variant="elevated"
                           :disabled="!invitationMessage"
                           @click="inviteUser">
                        Отправить приглашение
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <v-dialog v-model="showEmailDialog"
                  min-width="300"
                  width="600"
                  transition="dialog-bottom-transition">
            <v-card class="text-primary ma-0">
                <v-card-title>
                    Пригласить пользователя
                </v-card-title>

                <v-card-text class="pa-4 py-0">
                    <v-form ref="mailForm"
                            @keyup.enter="inviteByEmail"
                            @submit.prevent>
                        <v-text-field class="mt-0 mb-1"
                                      readonly
                                      label="Заголовок приглашения"
                                      :modelValue="invitationTitle" />
                        <v-text-field class="mt-0 mb-1"
                                      autofocus
                                      label="Сообщение приглашения"
                                      :rules="textRules"
                                      v-model.trim="invitationMessage" />
                        <v-text-field class="mt-0 mb-1"
                                      label="Электронная почта"
                                      :rules="emailRules"
                                      v-model.trim="invitedUserEmail" />
                    </v-form>
                </v-card-text>

                <v-card-actions class="mb-2 mr-2">
                    <v-spacer />
                    <v-btn color="secondary"
                           variant="elevated"
                           @click="showEmailDialog = false">
                        Отменить
                    </v-btn>
                    <v-btn color="primary"
                           variant="elevated"
                           :disabled="!isValid"
                           @click="inviteByEmail">
                        Отправить приглашение
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
    import { mapState } from "vuex";

    import SelectGroupsMemberDialogs from "@/components/SelectGroupsMemberDialogs.vue";
    import GroupInfoList from "@/components/GroupInfoList.vue";

    import PagesNavigation from "@/common/pagesNavigation";
    import DataValidator from "@/common/dataValidator";
    import { DateTime } from "luxon";

    export default {
        props: {
            groupGuid: {
                type: String,
                required: true
            }
        },

        components: {
            SelectGroupsMemberDialogs,
            GroupInfoList
        },

        data() {
            return {
                group: null,
                members: [],

                showInviteDialog: false,

                showEmailDialog: false,
                invitedUserEmail: null,
                invitationMessage: null,

                showSelectGroupMemberDialog: false,
                selectedUser: null,

                showInviteUserDialog: false,

                textRules: [
                    v => !!v || "Нужно заполнить поле"
                ],
                emailRules: DataValidator.getEmailRules(),

                isValidMailForm: false
            };
        },

        created() {
            this.$store.commit("startLoading");
            this.$store.state.groupsService.getGroupWithUsers(this.groupGuid)
                .then(response => {
                    this.$store.commit("setSelectedGroup", response.data);
                    this.group = response.data;
                    this.members = response.data.members
                        .map((e, index) => ({
                            number: index + 1,
                            userName: e.userName,
                            rawItem: e
                        }));
                })
                .catch(error => this.$store.commit("error", error.message))
                .finally(() => this.$store.commit("stopLoading"));

            if (this.$store.state.sentInvitations.length === 0) {
                this.$store.dispatch("loadSentInvitations");
            }
        },

        computed: {
            ...mapState(["currentLocale"]),

            userGroups() {
                return this.$store.state.userGroups;
            },

            sentInvitations() {
                return this.$store.state.sentInvitations;
            },

            invitedUsersIds() {
                const ids = this.group?.members?.map(e => e.id) ?? [];

                this.sentInvitations
                    .filter(e => e.group.id === this.group?.id)
                    .map(e => e.recipient.id)
                    .forEach(id1 => {
                        if (!ids.some(id2 => id2 === id1)) {
                            ids.push(id1);
                        }
                    });

                return ids;
            },

            invitationTitle() {
                if (!this.group) {
                    return null;
                }

                return `Приглашение в группу "${this.group.name}" [${this.getMiniGuid(this.group.guid)}]`;
            },

            isCompleteMailForm() {
                return !!this.invitationMessage && !!this.invitedUserEmail;
            },

            isValid() {
                if (this.isCompleteMailForm) {
                    this.validateMailForm();
                }

                return this.isCompleteMailForm && this.isValidMailForm;
            }
        },

        watch: {

        },

        methods: {
            getMiniGuid(guid) {
                return guid.substring(0, 8);
            },

            async validateMailForm() {
                if (!this.$refs.mailForm) {
                    return;
                }

                const { valid } = await this.$refs.mailForm.validate();

                this.isValidMailForm = valid;
            },

            onShowGroupDialog() {
                if (this.userGroups.length === 0) {
                    this.$store.dispatch("loadUserGroups");
                }

                this.showSelectGroupMemberDialog = true;
                this.selectedUser = null;
            },
            onSelectUser(user) {
                this.selectedUser = user;
                this.showInviteUserDialog = true;
            },

            inviteUser() {
                const request = {
                    title: this.invitationTitle,
                    message: this.invitationMessage,
                    recipientId: this.selectedUser.id,
                    recipientEmail: null,
                    groupId: this.group.id
                };

                this.$store.dispatch("createInvitation", request)
                    .then(() => this.$store.dispatch("loadSentInvitations"));

                this.showInviteDialog = false;
                this.showSelectGroupMemberDialog = false;
                this.showSelectUserDialog = false;
                this.showInviteUserDialog = false;

                this.invitationMessage = null;
                this.selectedGroup = null;
                this.selectedUser = null;
            },

            inviteByEmail() {
                const request = {
                    title: this.invitationTitle,
                    message: this.invitationMessage,
                    recipientId: null,
                    recipientEmail: this.invitedUserEmail,
                    groupId: this.group.id
                };

                this.$store.dispatch("createInvitation", request)
                    .then(() => this.$store.dispatch("loadSentInvitations"));

                this.showInviteDialog = false;
                this.showEmailDialog = false;
                this.invitationMessage = null;
                this.invitedUserEmail = null;
            },

            getDate(isoDateTime) {
                return DateTime.fromISO(isoDateTime).setLocale(this.currentLocale).toFormat("yyyy.MM.dd");
            },
            getTime(isoDateTime) {
                return DateTime.fromISO(isoDateTime).setLocale(this.currentLocale).toFormat("HH:mm");
            },

            getHeaders() {
                return [
                    {
                        title: "Номер в группе",
                        align: "end",
                        sortable: true,
                        key: "number",
                        width: 175
                    },
                    {
                        title: "Имя",
                        align: "start",
                        sortable: true,
                        key: "userName"
                    }
                ];
            },

            goBack: PagesNavigation.goBack
        }
    };
</script>

<style lang="scss">
    .group-info-view .group-info div.text-primary {
        width: 210px;
    }

    .group-info-view .group-name {
        color: rgb(var(--v-theme-primary)) !important;
    }

    .group-info-view .group-description {
        color: rgb(var(--v-theme-lonely)) !important;
    }

    .group-info-view .v-data-table-header__content,
    .group-info-view .v-data-table-footer {
        color: rgb(var(--v-theme-primary)) !important;
        font-weight: bold;
    }
</style>